/**
 * @graphql cc
 */
import gql from 'graphql-tag';
import type { FC } from 'react';
import React from 'react';
import type { QueryResult } from 'react-apollo';
import { ApolloProvider } from 'react-apollo';
import {
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- Read http://go/cfe-no-import-apollo-functions
	useQuery,
} from '@apollo/react-hooks';

import { getApolloClient } from '@confluence/graphql/entry-points/getApolloClient.ts';
import {
	getExperienceName,
	getMacroAttributesFromADFNode,
} from '@confluence/macro-tracker/entry-points/MacroExperienceTracker.ts';
import { MacroExperienceFailure } from '@confluence/macro-tracker/entry-points/MacroExperienceFailure.ts';
import { MacroExperienceSuccess } from '@confluence/macro-tracker/entry-points/MacroExperienceSuccess.ts';
import type { MacroExperienceBase } from '@confluence/macro-tracker/entry-points/types.ts';
/* eslint-disable no-restricted-imports */
import { ExperienceTrackerContext } from '@confluence/experience-tracker/entry-points/component.ts';
import { getExperienceTracker } from '@confluence/experience-tracker/entry-points/getExperienceTracker.ts';

import type { InlineImageProps } from './types.ts';
import type {
	InlineImageQuery as InlineImageQuery$data,
	InlineImageQueryVariables as InlineImageQuery$variables,
} from './__types__/InlineImageQuery.ts';
import { InlineImageComponent } from './InlineImageComponent.tsx';

const InlineImageWithApolloProvider: FC<InlineImageProps & MacroExperienceBase> = ({
	link,
	id,
	collection,
	contentId,
	mode,
	extensionKey,
	node,
	...props
}: InlineImageProps & MacroExperienceBase) => {
	const { data, loading, error }: QueryResult = useQuery<
		InlineImageQuery$data,
		InlineImageQuery$variables
	>(
		gql`
			query InlineImageQuery($contentId: ID) {
				content(id: $contentId) {
					nodes {
						id
						body {
							atlas_doc_format {
								mediaToken {
									token
								}
							}
						}
					}
				}
				mediaConfiguration {
					clientId
					fileStoreUrl
				}
			}
		`,
		{
			variables: { contentId },
		},
	);

	if (loading) {
		return null;
	}

	const token = data?.content?.nodes?.[0]?.body?.atlas_doc_format?.mediaToken?.token;
	const clientId = data?.mediaConfiguration?.clientId;
	const fileStoreUrl = data?.mediaConfiguration?.fileStoreUrl;
	const attributes = getMacroAttributesFromADFNode(node);

	return (
		// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
		<div data-testid="inline-image" style={{ display: 'inline-block' }}>
			<InlineImageComponent
				{...props}
				id={id}
				collection={collection}
				link={link}
				token={token}
				clientId={clientId}
				baseUrl={fileStoreUrl}
			/>
			{error ? (
				<MacroExperienceFailure
					name={getExperienceName(mode, node)}
					contentId={contentId}
					extensionKey={extensionKey}
					mode={mode}
					error={
						error || new Error(`${extensionKey} failed to render. InlineImage query data is empty`)
					}
					attributes={attributes}
					source="InlineImage"
				/>
			) : (
				<MacroExperienceSuccess
					name={getExperienceName(mode, node)}
					contentId={contentId}
					extensionKey={extensionKey}
					mode={mode}
					attributes={attributes}
				/>
			)}
		</div>
	);
};

export const InlineImage = (props) => (
	<ApolloProvider client={getApolloClient()}>
		{/* eslint-disable-next-line no-restricted-imports */}
		<ExperienceTrackerContext.Provider value={getExperienceTracker()}>
			<InlineImageWithApolloProvider {...props} />
		</ExperienceTrackerContext.Provider>
	</ApolloProvider>
);
